import { RefObject, useCallback, useEffect, useState } from 'react';
import { useIsClient } from '@fiverr-private/hooks';
import { usePageVisibility } from '../../../../shared/hooks/usePageVisibility';
import { usePrefersReducedMotion } from '../../../../shared/hooks/usePrefersReducedMotion';
import { PlaceholderFadeOptions, runPlaceholderFade } from './runPlaceholderFade';
import { fadingPlaceholderClass, hiddenPlaceholderClass } from './styles.ve.css';

interface Options extends PlaceholderFadeOptions {
    staticText: string;
    ariaLabel?: string;
    active?: boolean;
    selector?: string;
}

const DEFAULT_SELECTOR = 'input';

export const usePlaceholderFade = (
    containerRef: RefObject<HTMLElement>,
    phrases: string[],
    { staticText, ariaLabel = staticText, active = true, selector = DEFAULT_SELECTOR, ...options }: Options
) => {
    const [isInteracting, setIsInteracting] = useState(false);
    const isClient = useIsClient();
    const isPageVisible = usePageVisibility();
    const prefersReducedMotion = usePrefersReducedMotion();

    const getInputs = useCallback(() => {
        const root = containerRef.current;

        if (!root) {
            return [];
        }

        const matched = Array.from(root.querySelectorAll<HTMLInputElement>(selector));

        return matched.length ? matched : Array.from(root.querySelectorAll<HTMLInputElement>(DEFAULT_SELECTOR));
    }, [containerRef, selector]);

    useEffect(() => {
        const inputs = getInputs();

        if (!inputs.length) {
            return undefined;
        }

        const handleFocus = () => setIsInteracting(true);
        const handleBlur = (event: FocusEvent) => setIsInteracting(Boolean((event.target as HTMLInputElement).value));

        inputs.forEach((input) => {
            input.setAttribute('aria-label', ariaLabel);
            input.classList.add(fadingPlaceholderClass);
            input.addEventListener('focus', handleFocus);
            input.addEventListener('blur', handleBlur);
        });

        return () =>
            inputs.forEach((input) => {
                input.classList.remove(fadingPlaceholderClass, hiddenPlaceholderClass);
                input.removeEventListener('focus', handleFocus);
                input.removeEventListener('blur', handleBlur);
            });
    }, [getInputs, ariaLabel]);

    const { holdMs, fadeMs } = options;
    const isRunning = Boolean(
        active && !isInteracting && isClient && isPageVisible && !prefersReducedMotion && phrases.length
    );

    useEffect(() => {
        if (!isRunning) {
            getInputs().forEach((input) => {
                input.classList.remove(hiddenPlaceholderClass);
                input.placeholder = staticText;
            });

            return undefined;
        }

        return runPlaceholderFade(
            phrases,
            {
                onShow: (text) =>
                    getInputs().forEach((input) => {
                        input.placeholder = text;
                        input.classList.remove(hiddenPlaceholderClass);
                    }),
                onHide: () => getInputs().forEach((input) => input.classList.add(hiddenPlaceholderClass)),
            },
            { holdMs, fadeMs }
        );
    }, [isRunning, phrases, getInputs, staticText, holdMs, fadeMs]);

    return isRunning;
};
